package org.gymly.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Checkbox
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.PortalRepo
import org.gymly.data.PortalTermsGuard
import org.gymly.model.PortalTerms
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.GymlyPrimaryButton
import org.gymly.ui.components.GymlySecondaryButton
import org.gymly.util.userMessage

/**
 * Antes de usar el portal o la app, el socio acepta la versión vigente de los términos y compromisos.
 * "No acepto" lo suspende hasta que el gimnasio lo reactive. Si el servidor no responde, no deja entrar.
 */
@Composable
fun PortalTermsGate(
    onLogout: () -> Unit,
    content: @Composable () -> Unit,
) {
    var terms by remember { mutableStateOf<PortalTerms?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var attempt by remember { mutableStateOf(0) }
    // Si una llamada del portal recibe TERMS_NOT_ACCEPTED o suspensión, se vuelve a consultar aquí.
    val revision by PortalTermsGuard.revision.collectAsState()

    LaunchedEffect(attempt, revision) {
        loadError = null
        try {
            terms = PortalRepo.api.fetchTerms()
        } catch (e: Throwable) {
            loadError = e.userMessage("No pudimos cargar los términos. Revisa tu conexión.")
        }
    }

    val current = terms
    when {
        current != null && current.suspended -> TermsSuspended(current, onLogout = onLogout)
        current != null && (current.accepted || !current.required) -> content()
        current != null -> PortalTermsAccept(
            terms = current,
            onAccepted = { terms = it },
            onLogout = onLogout,
        )
        loadError != null -> Column(
            Modifier.fillMaxSize().padding(GymlySpace.lg),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(loadError!!, color = GymlyColors.Error, textAlign = TextAlign.Center)
            Spacer(Modifier.height(GymlySpace.md))
            GymlyPrimaryButton(text = "Reintentar", onClick = { attempt++ })
            Spacer(Modifier.height(GymlySpace.sm))
            TextButton(onClick = onLogout) { Text("Cerrar sesión") }
        }
        else -> GymlyLoadingCenter()
    }
}

/** Pantalla de suspendido: por rechazo de los términos o por decisión administrativa. */
@Composable
private fun TermsSuspended(terms: PortalTerms, onLogout: () -> Unit) {
    Column(
        Modifier.fillMaxSize().padding(GymlySpace.lg),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Acceso suspendido", fontWeight = FontWeight.Bold, fontSize = 20.sp)
        Spacer(Modifier.height(GymlySpace.sm))
        val motivo = if (terms.suspensionReason == "ADMIN") {
            "El gimnasio suspendió tu acceso${terms.suspensionNote?.let { ": $it" } ?: "."} " +
                "Comunícate con recepción."
        } else {
            "Rechazaste los términos y compromisos, así que tu acceso está suspendido. " +
                "Comunícate con el gimnasio para reactivarlo."
        }
        Text(motivo, color = GymlyColors.TextMuted, fontSize = 14.sp, textAlign = TextAlign.Center)
        Spacer(Modifier.height(GymlySpace.lg))
        GymlySecondaryButton(text = "Cerrar sesión", onClick = onLogout)
    }
}

@Composable
private fun PortalTermsAccept(
    terms: PortalTerms,
    onAccepted: (PortalTerms) -> Unit,
    onLogout: () -> Unit,
) {
    var marketing by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Column(
        Modifier
            .fillMaxSize()
            .padding(GymlySpace.lg),
    ) {
        Text(terms.title, fontWeight = FontWeight.Bold, fontSize = 20.sp)
        Spacer(Modifier.height(GymlySpace.xs))
        Text("Lee estas condiciones antes de continuar.", color = GymlyColors.TextMuted, fontSize = 13.sp)
        Spacer(Modifier.height(GymlySpace.md))
        TermsText(Modifier.weight(1f), terms.body)

        Spacer(Modifier.height(GymlySpace.md))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Checkbox(checked = marketing, onCheckedChange = { marketing = it }, enabled = !saving)
            Text("Quiero recibir promociones del gimnasio por WhatsApp (opcional).", fontSize = 13.sp)
        }
        error?.let {
            Text(it, color = GymlyColors.Error, fontSize = 13.sp, modifier = Modifier.padding(top = 6.dp))
        }
        Spacer(Modifier.height(GymlySpace.sm))
        GymlyPrimaryButton(
            text = "Acepto y continuar",
            loading = saving,
            onClick = {
                scope.launch {
                    saving = true
                    error = null
                    try {
                        onAccepted(PortalRepo.api.acceptTerms(terms.version, marketing))
                    } catch (e: Throwable) {
                        error = e.userMessage("No se pudo guardar tu aceptación. Inténtalo de nuevo.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        Spacer(Modifier.height(GymlySpace.sm))
        GymlySecondaryButton(
            text = "No acepto",
            onClick = {
                scope.launch {
                    saving = true
                    error = null
                    try {
                        // El servidor registra el rechazo y suspende el acceso; después se cierra la sesión.
                        PortalRepo.api.declineTerms(terms.version)
                        onLogout()
                    } catch (e: Throwable) {
                        error = e.userMessage("No se pudo registrar tu respuesta. Inténtalo de nuevo.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
    }
}

/**
 * Perfil → Términos. El socio vuelve a leer el texto vigente, ve su estado y cambia su permiso de WhatsApp.
 */
@Composable
fun PortalTermsDetail(onBack: () -> Unit, onLogout: () -> Unit) {
    var terms by remember { mutableStateOf<PortalTerms?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var marketing by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var attempt by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(attempt) {
        loadError = null
        try {
            val t = PortalRepo.api.fetchTerms()
            terms = t
            marketing = t.marketingWhatsApp
        } catch (e: Throwable) {
            loadError = e.userMessage("No pudimos cargar los términos.")
        }
    }

    val current = terms
    // Suspendido: mensaje de suspensión, centrado en el área principal.
    if (current != null && current.suspended) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
            Box(Modifier.widthIn(max = 760.dp).fillMaxSize()) { TermsSuspended(current, onLogout = onLogout) }
        }
        return
    }
    // Sin aceptar: el formulario de aceptación, centrado. El menú queda bloqueado hasta aceptar.
    if (current != null && current.required && !current.accepted) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
            Box(Modifier.widthIn(max = 760.dp).fillMaxSize()) {
                PortalTermsAccept(
                    terms = current,
                    onAccepted = {
                        terms = it
                        PortalTermsGuard.requestRecheck()
                    },
                    onLogout = onLogout,
                )
            }
        }
        return
    }

    Column(Modifier.fillMaxSize().padding(GymlySpace.lg)) {
        TextButton(onClick = onBack) { Text("← Volver al perfil") }
        when {
            current == null && loadError == null -> GymlyLoadingCenter()
            loadError != null -> Column {
                Text(loadError!!, color = GymlyColors.Error)
                Spacer(Modifier.height(GymlySpace.sm))
                GymlyPrimaryButton(text = "Reintentar", onClick = { attempt++ })
            }
            current != null -> Column(Modifier.fillMaxSize()) {
                Text(current.title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Spacer(Modifier.height(4.dp))
                Text(
                    if (current.accepted) "Aceptaste esta versión (${current.version})."
                    else "Aún no has aceptado esta versión.",
                    color = if (current.accepted) GymlyColors.TextMuted else GymlyColors.Error,
                    fontSize = 13.sp,
                )
                Spacer(Modifier.height(GymlySpace.md))
                TermsText(Modifier.weight(1f), current.body)
                Spacer(Modifier.height(GymlySpace.md))
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = marketing,
                        onCheckedChange = { marketing = it },
                        enabled = !saving && current.accepted,
                    )
                    Text("Recibir promociones por WhatsApp", fontSize = 13.sp)
                }
                message?.let {
                    Text(it, color = GymlyColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(top = 4.dp))
                }
                if (current.accepted) {
                    Spacer(Modifier.height(GymlySpace.sm))
                    GymlyPrimaryButton(
                        text = "Guardar mi permiso",
                        loading = saving,
                        onClick = {
                            scope.launch {
                                saving = true
                                message = null
                                try {
                                    terms = PortalRepo.api.acceptTerms(current.version, marketing)
                                    message = "Guardado."
                                } catch (e: Throwable) {
                                    message = e.userMessage("No se pudo guardar.")
                                } finally {
                                    saving = false
                                }
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun TermsText(modifier: Modifier, markdown: String) {
    Column(modifier.verticalScroll(rememberScrollState())) {
        termsLines(markdown).forEach { line ->
            Text(
                line.text,
                fontSize = if (line.heading) 15.sp else 14.sp,
                fontWeight = if (line.heading) FontWeight.SemiBold else null,
                modifier = Modifier.padding(vertical = 3.dp),
            )
        }
    }
}

/** Una línea del documento; los títulos (##) salen en negrita. Se quitan las marcas de Markdown. */
internal data class TermsLine(val text: String, val heading: Boolean)

internal fun termsLines(markdown: String): List<TermsLine> =
    markdown.lines()
        .filter { it.isNotBlank() && !it.startsWith("# ") && !it.startsWith("Versión ") }
        .map { raw ->
            val heading = raw.startsWith("## ")
            val text = raw.removePrefix("## ").removePrefix("- ").replace("**", "")
            TermsLine(if (raw.startsWith("- ")) "• $text" else text, heading)
        }
